'use client'

import { type LinkProps } from 'next/link'
import { useSearchParams } from 'next/navigation'
import { PropsWithChildren, MouseEvent, ReactNode } from 'react'

import { Link, usePathname } from '@/shared/i18n'
import { cn, isMatch } from '@/shared/lib'
import { NewBadge } from '@/shared/ui'

export const List = ({ children }: PropsWithChildren) => {
  return (
    <nav>
      <ul className="flex items-center gap-6">{children}</ul>
    </nav>
  )
}

type ItemProps = Pick<LinkProps, 'href'> & {
  accessible?: boolean
  newBadge?: boolean
  onNotAccessible?: (pathname: string) => void
  excludedPaths: string[]
  children: ReactNode
  className?: string
}

export const Item = ({
  href,
  newBadge = false,
  className,
  accessible = true,
  onNotAccessible,
  excludedPaths,
  children,
  ...restProps
}: PropsWithChildren<ItemProps>) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const hrefString = typeof href === 'string' ? href : href.pathname || ''
  const selected = (() => {
    let isSelected = pathname ? pathname.startsWith(hrefString) : false

    if (!isSelected) {
      return isSelected
    }

    if (pathname && excludedPaths.length) {
      isSelected = !isMatch(pathname || '', excludedPaths)
    }

    return isSelected
  })()

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!accessible) {
      event.preventDefault()
      onNotAccessible?.(hrefString)

      return
    }

    const isEmptySearchParams = !searchParams?.toString()

    if (pathname === hrefString && isEmptySearchParams) {
      event.preventDefault()
    }
  }

  return (
    <li>
      <Link
        href={href}
        onClick={handleClick}
        className={cn(
          'typo-body1 flex h-10 w-[80px] items-center justify-center rounded-sm py-[7px] font-medium text-black transition-all',
          'hover:bg-purple-50',
          selected && 'font-bold',
        )}
        {...(selected && { 'aria-current': 'page' })}
        {...restProps}
      >
        <div
          className={cn(
            '-mb-0.5 box-border flex h-fit w-fit items-center gap-[3px] border-b-2 border-transparent',
            selected && 'border-black',
            className,
          )}
        >
          <span>{children}</span>
          {newBadge && <NewBadge />}
        </div>
      </Link>
    </li>
  )
}
